import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  createContext,
  FC,
  PropsWithChildren,
} from 'react';
import {
  mediaMobileOnly,
  mediaTabletAndUp,
  mediaDesktopAndUp,
  mediaExtraLargeScreens,
} from '../constants/customMedia';

interface ValuesByQuery<V extends unknown> {
  [query: string]: V;
}

const context = createContext(new Map());
const getQueryString = <V extends unknown>(query: ValuesByQuery<V>) =>
  Object.entries(query)
    .map(([key, value]) => `${key},${JSON.stringify(value)}`)
    .join(',');

export const ExpensiveMediaProvider: FC<PropsWithChildren> = ({children}) => {
  const [cache] = useState(() => new Map());
  return <context.Provider value={cache}>{children}</context.Provider>;
};

/**
 * Allows to toggle javascript based on media queries.
 * It's expensive as this can trigger multiple re-renderings and will always render once the default value. It's recommended to avoid and better use CSS instead.
 */
export const useExpensiveMedia = <V extends unknown>(
  valuesByQuery: ValuesByQuery<V>,
  defaultValue: V,
): V => {
  const cache = useContext(context);
  const [value, setValue] = useState(
    () => cache.get(getQueryString<V>(valuesByQuery)) ?? defaultValue,
  );

  useEffect(() => {
    const mediaQueryLists = Object.keys(valuesByQuery).map(queryString => ({
      mql: window.matchMedia(queryString),
      key: queryString,
    }));
    const getValue = () => {
      let query: string | null = null;
      for (const {key, mql} of mediaQueryLists) {
        if (mql.matches) {
          query = key;
        }
      }

      return query ? valuesByQuery[query]! : defaultValue;
    };

    const update = () => {
      cache.set(getQueryString(valuesByQuery), getValue());
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setValue(getValue);
    };

    update();
    mediaQueryLists.forEach(({mql}) => mql.addListener(update));
    return () => mediaQueryLists.forEach(({mql}) => mql.removeListener(update));
  }, [defaultValue, valuesByQuery]);

  return value;
};

/**
 * Allows to toggle javascript based on media queries.
 * It's expensive as this can trigger multiple re-renderings and will always render once the default value. It's recommended to avoid and better use CSS instead.
 */
export const useExpensiveValueByViewport = <V extends unknown>(
  values: {small?: V; medium?: V; large?: V; extraLarge?: V},
  defaultValue: V,
): V => {
  const viewportQuery = useMemo(() => {
    return {
      [mediaMobileOnly]: values.small ?? defaultValue, // => mobile viewport
      [mediaTabletAndUp]: values.medium ?? defaultValue, // => tablet viewport
      [mediaDesktopAndUp]: values.large ?? defaultValue, // => desktop viewport
      [mediaExtraLargeScreens]:
        values.extraLarge ?? values.large ?? defaultValue, // => desktop viewport
    };
  }, [
    values.small,
    values.medium,
    values.large,
    values.extraLarge,
    defaultValue,
  ]);
  return useExpensiveMedia(viewportQuery, defaultValue);
};
